// The pipeline board's view-model (WEB_PLAN.md §5.9): a port of what PipelineView.swift computes for its header,
// pipeline menu, owner menu and kanban columns, on top of domain/pipelines.ts. Pure (no React, no DOM) so the page
// stays a thin renderer and the rules are unit-tested in test/unit/screens.pipeline.board.test.ts.
//
// The board reads `useVisibleRecords().values`: decodable stores only, which is what an iPhone's board shows and
// counts. Phase 1 is read-only, so nothing here builds a patch.

import { RECORD_TYPES, RECORD_TYPE_PLURALS, type RecordType } from '@/contracts/enums';
import type { PipelinePayload, RecordPayload, StagePayload, UserPayload } from '@/contracts/models';
import { idKey, isUuid, sameId } from '@/contracts/uuid';
import { formatFixed, formatGrouped, formatMoney } from '@/domain/format';
import {
  defaultPipelineForBoard,
  pipelineById,
  pipelineColumns,
  pipelineRecords,
  pipelinesForType,
  pipelineTotals,
  recordsWithoutStage,
  type ModifiedAtLookup,
} from '@/domain/pipelines';
import { EMPTY_FILTER, filterToQuery } from '@/domain/records';
import { compareLocalized, fullName, lookup } from '@/domain/util';

// MARK: - Selection (kept in the URL so a board can be shared and survives a reload)

export const BOARD_QUERY = { pipeline: 'pipeline', owner: 'owner' } as const;

export interface BoardSelection {
  /** The pipeline on the board: the one in the URL when it still exists, else `defaultPipelineForBoard`. */
  pipeline: PipelinePayload | undefined;
  /** Owner filter (any case); null = Everyone. */
  ownerId: string | null;
}

/** Reads `?pipeline=<id>&owner=<id>`; a deleted pipeline or a malformed id falls back to the defaults. */
export function readBoardSelection(params: URLSearchParams, pipelines: readonly PipelinePayload[]): BoardSelection {
  const pipeline = pipelineById(pipelines, params.get(BOARD_QUERY.pipeline)) ?? defaultPipelineForBoard(pipelines);
  const owner = params.get(BOARD_QUERY.owner);
  return { pipeline, ownerId: owner !== null && isUuid(owner) ? owner : null };
}

/** The URL query after a menu choice; other parameters are kept. `null` removes the key (Everyone / default). */
export function writeBoardSelection(
  current: URLSearchParams,
  change: { pipelineId?: string | null; ownerId?: string | null },
): URLSearchParams {
  const next = new URLSearchParams(current);
  const put = (key: string, value: string | null | undefined) => {
    if (value === undefined) return;
    if (value === null) next.delete(key);
    else next.set(key, idKey(value));
  };
  put(BOARD_QUERY.pipeline, change.pipelineId);
  put(BOARD_QUERY.owner, change.ownerId);
  return next;
}

// MARK: - Pipeline menu (iOS `pipelineMenu`: one section per record type with pipelines)

export interface PipelineMenuGroup {
  type: RecordType;
  /** "Leads" / "Accounts" / "Contacts". */
  title: string;
  /** `pipelines(for:)`: the default first, then by name. */
  pipelines: PipelinePayload[];
}

export function pipelineMenuGroups(pipelines: readonly PipelinePayload[]): PipelineMenuGroup[] {
  return RECORD_TYPES.map((type) => ({ type, title: RECORD_TYPE_PLURALS[type], pipelines: pipelinesForType(pipelines, type) })).filter(
    (g) => g.pipelines.length > 0,
  );
}

/** A pipeline's name for menus and headings (a blank name would leave an empty button). */
export function pipelineTitle(p: Pick<PipelinePayload, 'name'> | undefined): string {
  const name = p?.name.trim() ?? '';
  return name !== '' ? name : p ? 'Untitled pipeline' : 'Pipeline';
}

/** A stage's name for column headers. */
export function stageTitle(s: Pick<StagePayload, 'name'>): string {
  const name = s.name.trim();
  return name !== '' ? name : 'Untitled stage';
}

// MARK: - Owner menu (iOS `ownerMenu` / `pipelineOwners`)

/**
 * `pipelineOwners`: people who own at least one visible store, by name. Inactive people are kept: their stores are
 * still on the board until someone reassigns them.
 */
export function pipelineOwners(visibleRecords: readonly RecordPayload[], users: readonly UserPayload[]): UserPayload[] {
  const ids = new Set<string>();
  for (const r of visibleRecords) if (r.ownerId) ids.add(idKey(r.ownerId));
  return users.filter((u) => ids.has(idKey(u.id))).sort((a, b) => compareLocalized(fullName(a), fullName(b)));
}

export interface OwnerOption {
  kind: 'everyone' | 'me' | 'person';
  /** The owner id to filter on; null for Everyone. */
  ownerId: string | null;
  label: string;
  inactive: boolean;
}

/**
 * Everyone, then "My stores" when the viewer has a profile, then (managers) everyone else who owns a visible store,
 * as on iOS.
 */
export function ownerOptions(owners: readonly UserPayload[], viewerId: string | null | undefined, isManager: boolean): OwnerOption[] {
  const out: OwnerOption[] = [{ kind: 'everyone', ownerId: null, label: 'Everyone', inactive: false }];
  if (viewerId) out.push({ kind: 'me', ownerId: viewerId, label: 'My stores', inactive: false });
  if (isManager) {
    for (const u of owners) {
      if (sameId(u.id, viewerId)) continue;
      out.push({ kind: 'person', ownerId: u.id, label: fullName(u) || 'Unnamed', inactive: !u.isActive });
    }
  }
  return out;
}

/** The owner button's text. */
export function ownerFilterLabel(
  ownerId: string | null | undefined,
  viewerId: string | null | undefined,
  users: ReadonlyMap<string, UserPayload>,
): string {
  if (!ownerId) return 'Everyone';
  if (sameId(ownerId, viewerId)) return 'My stores';
  const u = lookup(users, ownerId);
  return u ? fullName(u) || 'Unnamed' : 'Unknown person';
}

// MARK: - Board

/** Columns longer than this render through TanStack Virtual; shorter ones stay plain (find-in-page still works). */
export const VIRTUALIZE_AFTER = 40;

export interface BoardColumn<R extends RecordPayload = RecordPayload> {
  /** React key: lower-case stage id plus position (a hand-edited pipeline may repeat an id). */
  key: string;
  stage: StagePayload;
  title: string;
  /** Hottest first: priority, then most recently modified (domain `pipelineColumns`). */
  records: R[];
  count: number;
  value: number;
  weightedValue: number;
  /** Share of the board's stores, 0–100. */
  percent: number;
  /** "12 stores". */
  countText: string;
  /** "42%" (`String(format: "%.0f%%")`). */
  percentText: string;
  virtualized: boolean;
}

export interface Board<R extends RecordPayload = RecordPayload> {
  pipeline: PipelinePayload;
  columns: BoardColumn<R>[];
  /** Every store on this pipeline's board for the owner filter, stage or not. */
  storeCount: number;
  /** Not in a won or lost stage (no stage counts as open). */
  open: number;
  /** Σ value × probability / 100 over open stores with a stage. */
  weighted: number;
  /** Stores on this pipeline whose stage is not one of its stages: in no column. */
  withoutStage: R[];
}

export interface BoardOptions {
  ownerId?: string | null;
  /** Row `modified_at` by id (`useVisibleRecords().modifiedAt`), the card tie-break. */
  modifiedAt?: ModifiedAtLookup;
}

export function buildBoard<R extends RecordPayload>(
  pipeline: PipelinePayload,
  records: readonly R[],
  pipelines: readonly PipelinePayload[],
  opts: BoardOptions = {},
): Board<R> {
  const ownerId = opts.ownerId ?? null;
  const onBoard = pipelineRecords(pipeline, records, pipelines, ownerId);
  // Over the board's own stores, the company-wide totals are exactly iOS openPipelineValue / weightedPipelineValue:
  // every store here resolves to this pipeline, so its stage is looked up in it.
  const totals = pipelineTotals(onBoard, pipelines);
  const columns = pipelineColumns(pipeline, onBoard, pipelines, { modifiedAt: opts.modifiedAt }).map(
    (c, i): BoardColumn<R> => ({
      key: `${idKey(c.stage.id)}:${i}`,
      stage: c.stage,
      title: stageTitle(c.stage),
      records: c.records,
      count: c.records.length,
      value: c.value,
      weightedValue: c.weightedValue,
      percent: c.percent,
      countText: storeCountText(c.records.length),
      percentText: `${formatFixed(c.percent, 0)}%`,
      virtualized: c.records.length > VIRTUALIZE_AFTER,
    }),
  );
  return {
    pipeline,
    columns,
    storeCount: onBoard.length,
    open: totals.open,
    weighted: totals.weighted,
    withoutStage: recordsWithoutStage(pipeline, onBoard, pipelines),
  };
}

// MARK: - Empty states (PipelineView's EmptyStates; pipelines are admin-write, so only admins are sent to Settings)

/** "No pipelines": the company has none configured. */
export function noPipelinesMessage(isAdmin: boolean): string {
  return isAdmin ? 'Create a pipeline with stages in Settings › Pipelines.' : 'Ask an admin to configure a pipeline.';
}

/** "No stages": the chosen pipeline has none, so the board has no columns. */
export function noStagesMessage(pipeline: Pick<PipelinePayload, 'name'>, isAdmin: boolean): string {
  const name = pipelineTitle(pipeline);
  return isAdmin ? `Add stages to ${name} in Settings › Pipelines.` : `Ask an admin to add stages to ${name}.`;
}

/** "0 stores" / "1 store" / "1,240 stores" (iOS "(N records)" under a column name). */
export function storeCountText(n: number): string {
  return `${formatGrouped(n)} store${n === 1 ? '' : 's'}`;
}

/** "3 stores without a stage". */
export function withoutStageText(n: number): string {
  return `${formatGrouped(n)} store${n === 1 ? '' : 's'} without a stage`;
}

/** What a screen reader hears for a column header: "Proposal, won stage, 12 stores, $45,000, 30% of stores". */
export function columnSummary(column: BoardColumn, currencyCode: string): string {
  return [
    column.title,
    column.stage.isWon ? 'won stage' : column.stage.isLost ? 'lost stage' : null,
    column.countText,
    formatMoney(column.value, currencyCode),
    `${column.percentText} of stores`,
  ]
    .filter((s): s is string => s !== null)
    .join(', ');
}

/** The Stores list filtered to this pipeline (and owner): where the stage-less stores can be worked on. */
export function storesListHref(pipelineId: string, ownerId: string | null | undefined): string {
  const query = filterToQuery({ ...EMPTY_FILTER, pipelineIds: [idKey(pipelineId)], ownerIds: ownerId ? [idKey(ownerId)] : [] });
  return `/stores?${query.toString()}`;
}

/** The stores in board order (column by column, top to bottom), then the ones without a stage: the CSV's rows. */
export function boardExportRecords<R extends RecordPayload>(board: Board<R>): R[] {
  return [...board.columns.flatMap((c) => c.records), ...board.withoutStage];
}
